import React, { useState, useEffect } from 'react';
import { useAuth } from './contexts/AuthContext';
import { AuthModal } from './components/AuthModal';
import { ProfilePage } from './components/ProfilePage';
import { ProfileDropdown } from './components/ProfileDropdown';
import { AdminPage } from './components/AdminPage';
import { AuthCallback } from './components/AuthCallback';
import { PluginDetailsModal } from './components/PluginDetailsModal';
import AuthErrorHandler from './components/AuthErrorHandler';
import AuthDebugInfo from './components/AuthDebugInfo';
import { useDownloadTracking } from './hooks/useDownloadTracking';
import { supabase } from './supabase';
import { usePluginBanners } from './hooks/usePluginBanners';
import { usePluginStatsManager } from './hooks/usePluginStatsManager';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';

// Auto-logout functionality - checks if server was restarted
const checkServerRestart = async () => {
  try {
    // Get current server start time
    const response = await fetch(window.location.origin + '/api/server-status');
    const serverData = await response.json();
    
    const currentServerStart = serverData.startTime;
    const lastKnownStart = localStorage.getItem('server_start_time');
    
    // If server restart detected, clear auth and force logout
    if (lastKnownStart && currentServerStart !== lastKnownStart) {
      await supabase.auth.signOut();
      localStorage.clear();
      sessionStorage.clear();
      window.location.reload();
    }
    
    // Update stored server start time
    localStorage.setItem('server_start_time', currentServerStart);
  } catch (error) {
    // If we can't reach server status endpoint, just store current timestamp
    const currentTime = Date.now().toString();
    const lastKnownStart = localStorage.getItem('server_start_time');
    
    if (!lastKnownStart) {
      localStorage.setItem('server_start_time', currentTime);
    }
  }
};


interface PluginLink {
  id: string;
  type: string;
  display_name: string;
  url: string;
  icon: string;
  enabled: boolean;
  created_at: string;
}

interface Plugin {
  id: string;
  name: string;
  description: string;
  lastUpdated: string;
  color: string;
  wiki_url?: string;
  discord_url?: string;
  profile_picture_url?: string;
  links?: PluginLink[];
  builds: {
    [key: string]: string[];
  };
}

const HomePage: React.FC = () => {
  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Hero Section */}
      <div className="text-center mb-12">
        <div className="relative inline-block">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-full blur-xl opacity-30 animate-pulse"></div>
          <div className="relative w-32 h-32 bg-theme-surface-light border-4 border-theme-border-light rounded-full flex items-center justify-center mx-auto mb-8">
            <img 
              src="/custom-icon.svg" 
              alt="Custom Icon" 
              className="w-24 h-24"
              style={{ filter: `brightness(0) saturate(100%) invert(1)` }}
            />
          </div>
        </div>
        
        <h1 className="text-6xl font-bold text-theme-text mb-6 bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
          Hi there!
        </h1>
        
        <p className="text-2xl text-theme-text-secondary mb-8 max-w-3xl mx-auto">
          Welcome to the official website of Junkeh's Plugins!
          You can view all of my plugins here with the dev builds aswell!
        </p>
        </div>

      {/* Feature Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
        <div className="group bg-gradient-to-br from-theme-surface to-theme-surface-light border-2 border-theme-border rounded-xl p-6 hover:border-blue-500 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-blue-500/20">
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 bg-blue-500/20 border-2 border-blue-500/30 rounded-xl flex items-center justify-center group-hover:bg-blue-500/30 transition-colors">
              <img 
                src="https://unpkg.com/pixelarticons@1.8.0/svg/human-handsup.svg" 
                alt="human-handsup" 
                className="w-8 h-8"
                style={{ filter: `brightness(0) saturate(100%) invert(1)` }}
              />
            </div>
          </div>
          <h3 className="text-2xl font-bold text-theme-text mb-3 text-center">You Matter</h3>
          <p className="text-theme-text-secondary text-center">Any Feature you want, I will try my best to add it! If you have any suggestions, please contact me on discord!</p>
          </div>

        <div className="group bg-gradient-to-br from-theme-surface to-theme-surface-light border-2 border-theme-border rounded-xl p-6 hover:border-purple-500 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-purple-500/20">
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 bg-purple-500/20 border-2 border-purple-500/30 rounded-xl flex items-center justify-center group-hover:bg-purple-500/30 transition-colors">
              <img 
                src="https://unpkg.com/pixelarticons@1.8.0/svg/coin.svg" 
                alt="coin" 
                className="w-8 h-8"
                style={{ filter: `brightness(0) saturate(100%) invert(1)` }}
              />
            </div>
          </div>
          <h3 className="text-2xl font-bold text-theme-text mb-3 text-center">Free</h3>
          <p className="text-theme-text-secondary text-center">Most of my plugins, if not all, are free to use!</p>
          </div>

        <div className="group bg-gradient-to-br from-theme-surface to-theme-surface-light border-2 border-theme-border rounded-xl p-6 hover:border-pink-500 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-pink-500/20">
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 bg-pink-500/20 border-2 border-pink-500/30 rounded-xl flex items-center justify-center group-hover:bg-pink-500/30 transition-colors">
              <img 
                src="https://unpkg.com/pixelarticons@1.8.0/svg/heart.svg" 
                alt="Heart" 
                className="w-8 h-8"
                style={{ filter: `brightness(0) saturate(100%) invert(1)` }}
              />
            </div>
          </div>
          <h3 className="text-2xl font-bold text-theme-text mb-3 text-center">Support</h3>
          <p className="text-theme-text-secondary text-center">I'll try my best to help you with any issues you have!</p>
        </div>
      </div>

      {/* Call to Action */}
      <div className="text-center">
        <div className="bg-gradient-to-r from-theme-surface to-theme-surface-light border-2 border-theme-border rounded-2xl p-8 max-w-2xl mx-auto">
          <h2 className="text-3xl font-bold text-theme-text mb-4">Ready to explore?</h2>
          <p className="text-theme-text-secondary mb-6">Check out our downloads section for amazing plugins and etc!</p>
          <div className="flex items-center justify-center gap-2 text-theme-text-muted">
            <img 
              src="https://unpkg.com/pixelarticons@1.8.0/svg/arrow-right.svg" 
              alt="Arrow" 
              className="w-5 h-5"
              style={{ filter: `brightness(0) saturate(100%) invert(60%)` }}
            />
            <span>Use the navigation above to get started</span>
          </div>
        </div>
      </div>


    </main>
  );
};

// Custom Star Components
const FullStar: React.FC = () => (
  <svg version="1.1" width="16" height="16" viewBox="0 0 260 260" xmlns="http://www.w3.org/2000/svg" shapeRendering="crispEdges">
    <rect x="120" y="0" width="20" height="200" fill="#fbbf24" />
    <rect x="110" y="30" width="10" height="180" fill="#fbbf24" />
    <rect x="140" y="30" width="10" height="180" fill="#fbbf24" />
    <rect x="100" y="60" width="10" height="160" fill="#fbbf24" />
    <rect x="150" y="60" width="10" height="160" fill="#fbbf24" />
    <rect x="0" y="90" width="100" height="10" fill="#fbbf24" />
    <rect x="160" y="90" width="100" height="10" fill="#fbbf24" />
    <rect x="10" y="100" width="90" height="10" fill="#fbbf24" />
    <rect x="160" y="100" width="90" height="10" fill="#fbbf24" />
    <rect x="30" y="110" width="70" height="10" fill="#fbbf24" />
    <rect x="160" y="110" width="70" height="10" fill="#fbbf24" />
    <rect x="40" y="120" width="60" height="10" fill="#fbbf24" />
    <rect x="160" y="120" width="60" height="10" fill="#fbbf24" />
    <rect x="50" y="130" width="50" height="10" fill="#fbbf24" />
    <rect x="160" y="130" width="50" height="10" fill="#fbbf24" />
    <rect x="70" y="140" width="30" height="10" fill="#fbbf24" />
    <rect x="160" y="140" width="30" height="10" fill="#fbbf24" />
    <rect x="80" y="150" width="20" height="90" fill="#fbbf24" />
    <rect x="160" y="150" width="20" height="90" fill="#fbbf24" />
    <rect x="70" y="160" width="10" height="100" fill="#fbbf24" />
    <rect x="180" y="160" width="10" height="100" fill="#fbbf24" />
    <rect x="60" y="190" width="10" height="70" fill="#fbbf24" />
    <rect x="190" y="190" width="10" height="70" fill="#fbbf24" />
    <rect x="50" y="220" width="10" height="40" fill="#fbbf24" />
    <rect x="200" y="220" width="10" height="40" fill="#fbbf24" />
    <rect x="40" y="240" width="10" height="20" fill="#fbbf24" />
    <rect x="80" y="240" width="10" height="10" fill="#fbbf24" />
    <rect x="170" y="240" width="10" height="10" fill="#fbbf24" />
    <rect x="210" y="240" width="10" height="20" fill="#fbbf24" />
  </svg>
);

const HalfStar: React.FC = () => (
  <svg version="1.1" width="16" height="16" viewBox="0 0 260 260" xmlns="http://www.w3.org/2000/svg" shapeRendering="crispEdges">
    <rect x="120" y="0" width="10" height="200" fill="#fbbf24" />
    <rect x="110" y="30" width="10" height="180" fill="#fbbf24" />
    <rect x="100" y="60" width="10" height="160" fill="#fbbf24" />
    <rect x="0" y="90" width="100" height="10" fill="#fbbf24" />
    <rect x="10" y="100" width="90" height="10" fill="#fbbf24" />
    <rect x="30" y="110" width="70" height="10" fill="#fbbf24" />
    <rect x="40" y="120" width="60" height="10" fill="#fbbf24" />
    <rect x="50" y="130" width="50" height="10" fill="#fbbf24" />
    <rect x="70" y="140" width="30" height="10" fill="#fbbf24" />
    <rect x="80" y="150" width="20" height="90" fill="#fbbf24" />
    <rect x="70" y="160" width="10" height="100" fill="#fbbf24" />
    <rect x="60" y="190" width="10" height="70" fill="#fbbf24" />
    <rect x="50" y="220" width="10" height="40" fill="#fbbf24" />
    <rect x="40" y="240" width="10" height="20" fill="#fbbf24" />
    <rect x="80" y="240" width="10" height="10" fill="#fbbf24" />
  </svg>
);

const EmptyStar: React.FC = () => (
  <svg version="1.1" width="16" height="16" viewBox="0 0 260 260" xmlns="http://www.w3.org/2000/svg" shapeRendering="crispEdges">
    <rect x="120" y="0" width="20" height="200" fill="#6b7280" />
    <rect x="110" y="30" width="10" height="180" fill="#6b7280" />
    <rect x="140" y="30" width="10" height="180" fill="#6b7280" />
    <rect x="100" y="60" width="10" height="160" fill="#6b7280" />
    <rect x="150" y="60" width="10" height="160" fill="#6b7280" />
    <rect x="0" y="90" width="100" height="10" fill="#6b7280" />
    <rect x="160" y="90" width="100" height="10" fill="#6b7280" />
    <rect x="10" y="100" width="90" height="10" fill="#6b7280" />
    <rect x="160" y="100" width="90" height="10" fill="#6b7280" />
    <rect x="30" y="110" width="70" height="10" fill="#6b7280" />
    <rect x="160" y="110" width="70" height="10" fill="#6b7280" />
    <rect x="40" y="120" width="60" height="10" fill="#6b7280" />
    <rect x="160" y="120" width="60" height="10" fill="#6b7280" />
    <rect x="50" y="130" width="50" height="10" fill="#6b7280" />
    <rect x="160" y="130" width="50" height="10" fill="#6b7280" />
    <rect x="70" y="140" width="30" height="10" fill="#6b7280" />
    <rect x="160" y="140" width="30" height="10" fill="#6b7280" />
    <rect x="80" y="150" width="20" height="90" fill="#6b7280" />
    <rect x="160" y="150" width="20" height="90" fill="#6b7280" />
    <rect x="70" y="160" width="10" height="100" fill="#6b7280" />
    <rect x="180" y="160" width="10" height="100" fill="#6b7280" />
    <rect x="60" y="190" width="10" height="70" fill="#6b7280" />
    <rect x="190" y="190" width="10" height="70" fill="#6b7280" />
    <rect x="50" y="220" width="10" height="40" fill="#6b7280" />
    <rect x="200" y="220" width="10" height="40" fill="#6b7280" />
    <rect x="40" y="240" width="10" height="20" fill="#6b7280" />
    <rect x="80" y="240" width="10" height="10" fill="#6b7280" />
    <rect x="170" y="240" width="10" height="10" fill="#6b7280" />
    <rect x="210" y="240" width="10" height="20" fill="#6b7280" />
  </svg>
);

// Star Rating Component
const StarRating: React.FC<{ rating: number }> = ({ rating }) => {
  const stars = [];
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;
  
  for (let i = 0; i < 5; i++) {
    if (i < fullStars) {
      stars.push(<FullStar key={i} />);
    } else if (i === fullStars && hasHalfStar) {
      stars.push(<HalfStar key={i} />);
    } else {
      stars.push(<EmptyStar key={i} />);
    }
  }
  
  return <div className="flex items-center gap-1">{stars}</div>;
};

const DownloadsPage: React.FC<{ hasDevAccess: boolean; plugins: Plugin[]; pluginsLoading: boolean; pluginStats: { [key: string]: any }; loadingStats: boolean; refreshPluginStats: (pluginId: string) => Promise<void> }> = ({ hasDevAccess, plugins, pluginsLoading, pluginStats, loadingStats, refreshPluginStats }) => {
  const [selectedPlugin, setSelectedPlugin] = useState<Plugin | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { getBannersForPlugin, getBannerStyles } = usePluginBanners();
  const { currentTheme } = useTheme();

  // Set plugin colors to theme secondary color
  const themedPlugins = plugins.map((plugin: Plugin) => ({
    ...plugin,
    color: currentTheme?.colors.secondary || '#8b5cf6'
  }));

  const openModal = (plugin: Plugin) => {
    setSelectedPlugin(plugin);
    setIsModalOpen(true);
  };

  // Helper function to get card icon position classes
  const getCardIconPositionClass = (position: string, placement: string) => {
    const positions: { [key: string]: string } = {
      'top-left': 'top-2 left-2',
      'top-center': 'top-2 left-1/2 transform -translate-x-1/2',
      'top-right': 'top-2 right-2',
      'middle-left': 'top-1/2 left-2 transform -translate-y-1/2',
      'middle-center': 'top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2',
      'middle-right': 'top-1/2 right-2 transform -translate-y-1/2',
      'bottom-left': 'bottom-2 left-2',
      'bottom-center': 'bottom-2 left-1/2 transform -translate-x-1/2',
      'bottom-right': 'bottom-2 right-2'
    };

    const placementAdjustment: { [key: string]: string } = {
      'outside': '-m-3',
      'inside': 'm-2',
      'overlay': ''
    };

    return `${positions[position] || positions['top-left']} ${placementAdjustment[placement] || ''}`;
  };

  // Helper function to get overlay layout classes
  const getOverlayLayoutClass = (banner: any) => {
    if (banner.overlay_text_align === 'center') return 'items-center justify-center';
    if (banner.overlay_text_align === 'right') return 'items-center justify-end';
    return 'items-center justify-start';
  };

  // Helper function to render overlay banner content
  const renderOverlayBannerContent = (banner: any) => {
    if (banner.is_warning) {
      return (
        <>
          <div className="flex items-center gap-2">
            {banner.icon_enabled && banner.icon_name && (
              <img 
                src={`https://unpkg.com/pixelarticons@1.8.0/svg/${banner.icon_name}.svg`}
                alt={banner.text}
                className="flex-shrink-0"
                style={{ 
                  filter: banner.icon_filter,
                  width: `${banner.icon_size || 24}px`,
                  height: `${banner.icon_size || 24}px`
                }}
              />
            )}
            <span className="font-semibold">{banner.text}</span>
          </div>
          {banner.warning_subtext && (
            <p className="text-xs opacity-75 ml-8 font-normal">
              {banner.warning_subtext}
            </p>
          )}
        </>
      );
    }

    // Handle different icon positions for overlay banners
    const iconElement = banner.icon_enabled && banner.icon_name ? (
      <img 
        src={`https://unpkg.com/pixelarticons@1.8.0/svg/${banner.icon_name}.svg`}
        alt={banner.text}
        className="flex-shrink-0"
        style={{ 
          filter: banner.icon_filter,
          width: `${banner.icon_size || 16}px`,
          height: `${banner.icon_size || 16}px`
        }}
      />
    ) : null;

    const textElement = <span className="font-semibold">{banner.text}</span>;

    switch (banner.overlay_icon_position) {
      case 'right':
        return (
          <>
            {textElement}
            {iconElement}
          </>
        );
      case 'both':
        return (
          <>
            {iconElement}
            {textElement}
            {iconElement}
          </>
        );
      case 'stationary-left':
        return (
          <div className="flex items-center justify-between w-full">
            {iconElement}
            {textElement}
          </div>
        );
      case 'stationary-right':
        return (
          <div className="flex items-center justify-between w-full">
            {textElement}
            {iconElement}
          </div>
        );
      case 'stationary-center':
        return (
          <div className="flex items-center justify-between w-full">
            <div></div>
            <div className="flex items-center gap-2">
              {iconElement}
              {textElement}
            </div>
            <div></div>
          </div>
        );
      case 'left':
      default:
        return (
          <>
            {iconElement}
            {textElement}
          </>
        );
    }
  };

  if (pluginsLoading) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-theme-primary"></div>
          <span className="ml-3 text-theme-text">Loading plugins...</span>
        </div>
      </div>
    );
  }

  if (plugins.length === 0) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-theme-text mb-4">No plugins available</h2>
          <p className="text-theme-text-secondary">
            No plugins have been created by administrators yet.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-theme-text mb-2">Plugin Downloads</h1>
        <p className="text-theme-text-secondary">
          Download plugins to enhance your server functionality
        </p>
      </div>

      {/* Plugin Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {themedPlugins
          .sort((a, b) => {
            // Sort by priority (plugins with higher priority banners appear first)
            const aBanners = getBannersForPlugin(a.id);
            const bBanners = getBannersForPlugin(b.id);
            
            const aMaxPriority = Math.max(
              ...Object.values(aBanners).flat().map(banner => banner.priority || 0),
              0
            );
            const bMaxPriority = Math.max(
              ...Object.values(bBanners).flat().map(banner => banner.priority || 0),
              0
            );
            
            if (aMaxPriority !== bMaxPriority) {
              return bMaxPriority - aMaxPriority; // Higher priority first
            }
            
            // If same priority, sort alphabetically
            return a.name.localeCompare(b.name);
          })
          .map((plugin) => {
          const bannersByPosition = getBannersForPlugin(plugin.id);
          
          // Check if any banners are card style
          const cardStyleBanners = [
            ...bannersByPosition.header,
            ...bannersByPosition.footer,
            ...bannersByPosition['left-vertical'],
            ...bannersByPosition['right-vertical']
          ].filter(banner => banner.banner_type === 'card');
          
          const hasCardStyle = cardStyleBanners.length > 0;
          const cardBanner = cardStyleBanners[0]; // Use first card style banner for card styling
          
          return (
            <div
              key={plugin.id}
              className={`bg-theme-surface rounded-lg hover:shadow-lg transition-all cursor-pointer group relative h-[420px] flex flex-col ${
                hasCardStyle 
                  ? 'border-2' 
                  : 'border border-theme-border'
              }`}
              style={hasCardStyle ? {
                borderColor: cardBanner.border_color,
                backgroundColor: cardBanner.background_color,
                borderStyle: cardBanner.border_style,
                borderWidth: `${cardBanner.border_width}px`,
                borderRadius: `${cardBanner.border_radius}px`
              } : {}}
              onClick={() => openModal(plugin)}
            >
              {/* Card Style Overlay (if card style banner exists) */}
              {hasCardStyle && (
                <div 
                  className="absolute inset-0 rounded-lg pointer-events-none"
                  style={{
                    backgroundColor: cardBanner.background_color,
                    borderRadius: `${cardBanner.border_radius}px`
                  }}
                />
              )}

              {/* Card Style Icon (if enabled and has card banner) */}
              {hasCardStyle && cardBanner.icon_enabled && cardBanner.icon && (
                <div 
                  className={`absolute z-20 ${getCardIconPositionClass(cardBanner.card_icon_position, cardBanner.card_icon_placement)}`}
                  style={{
                    fontSize: `${cardBanner.icon_size}px`
                  }}
                >
                  <img 
                    src={`https://unpkg.com/pixelarticons@1.8.0/svg/${cardBanner.icon}.svg`}
                    alt="card icon"
                    className="drop-shadow-lg"
                    style={{ 
                      filter: cardBanner.icon_filter,
                      width: `${cardBanner.icon_size}px`,
                      height: `${cardBanner.icon_size}px`
                    }}
                  />
                </div>
              )}

              {/* Left Vertical Banners - Fixed positioning with padding */}
              <div className="flex flex-1 min-h-0">
                {bannersByPosition['left-vertical'].length > 0 && bannersByPosition['left-vertical'][0].banner_type !== 'card' && (
                  <div className="flex flex-col justify-center py-4 pl-2 space-y-2 flex-shrink-0">
                    {bannersByPosition['left-vertical'].map(banner => {
                      const { styles, className } = getBannerStyles(banner);
                      return (
                        <div key={banner.id} className="transform -rotate-90 origin-center">
                          <div 
                            className={`flex items-center gap-2 border-2 whitespace-nowrap ${className}`}
                            style={styles}
                          >
                            {banner.icon_enabled && banner.icon_name && (
                              <img 
                                src={`https://unpkg.com/pixelarticons@1.8.0/svg/${banner.icon_name}.svg`}
                                alt={banner.text}
                                className="flex-shrink-0"
                                style={{ 
                                  filter: banner.icon_filter,
                                  width: `${banner.icon_size}px`,
                                  height: `${banner.icon_size}px`
                                }}
                              />
                            )}
                            <span className="font-semibold text-xs">{banner.text}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Main Content Area - Takes full height and uses flexbox */}
                <div className={`flex-1 p-6 flex flex-col min-h-0 ${hasCardStyle ? 'relative z-10' : ''}`}>
                  {/* Header Banners Area - Fixed height to ensure consistent layout */}
                  <div className="w-full h-16 mb-4 flex-shrink-0">
                    {bannersByPosition.header.length > 0 && bannersByPosition.header.filter(b => b.banner_type !== 'card').length > 0 && bannersByPosition.header.filter(b => b.banner_type !== 'card').map(banner => {
                      const { styles, className } = getBannerStyles(banner);
                      const alignmentClass = 
                        banner.alignment === 'center' ? 'justify-center' :
                        banner.alignment === 'right' ? 'justify-end' : 'justify-start';
                      
                      return (
                        <div key={banner.id} className={`w-full flex ${alignmentClass} mb-2`}>
                          <div 
                            className={`flex ${banner.is_warning ? 'flex-col' : getOverlayLayoutClass(banner)} gap-2 border-2 ${banner.full_width ? 'w-full' : ''} rounded-lg ${className}`}
                            style={styles}
                          >
                            {renderOverlayBannerContent(banner)}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Plugin Content - Fixed size container */}
                  <div className="flex-1 flex flex-col min-h-0">
                    {/* Plugin Header - Fixed height */}
                    <div className="flex items-center gap-4 mb-4 flex-shrink-0">
                      <div className="w-16 h-16 rounded-lg overflow-hidden border border-theme-border flex items-center justify-center flex-shrink-0">
                        {plugin.profile_picture_url ? (
                          <img 
                            src={plugin.profile_picture_url}
                            alt={`${plugin.name} avatar`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              // Fallback to initials if image fails to load
                              const target = e.target as HTMLImageElement;
                              const parent = target.parentElement;
                              if (parent) {
                                parent.innerHTML = `
                                  <div class="w-full h-full flex items-center justify-center text-2xl font-bold text-white" style="background-color: ${plugin.color}">
                                    ${plugin.name[0]}
                                  </div>
                                `;
                              }
                            }}
                          />
                        ) : (
                          <div 
                            className="w-full h-full flex items-center justify-center text-2xl font-bold text-white"
                            style={{ backgroundColor: plugin.color }}
                          >
                            {plugin.name[0]}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-xl font-bold text-theme-text group-hover:text-theme-primary transition-colors truncate">
                          {plugin.name}
                        </h3>
                        <p className="text-theme-text-secondary text-sm line-clamp-2">
                          {plugin.description}
                        </p>
                      </div>
                    </div>

                    {/* Stats - Fixed height */}
                    {!loadingStats && pluginStats[plugin.id] && (
                      <div className="space-y-2 mb-4 flex-shrink-0">
                        {/* Rating */}
                        <div className="flex items-center gap-2">
                          <StarRating rating={pluginStats[plugin.id].average_rating || 0} />
                          <span className="text-sm text-theme-text-muted">
                            {pluginStats[plugin.id].average_rating ? 
                              `${pluginStats[plugin.id].average_rating.toFixed(1)} (${pluginStats[plugin.id].total_ratings})` : 
                              'No ratings'
                            }
                          </span>
                        </div>

                        {/* Downloads */}
                        <div className="flex items-center gap-2 text-sm text-theme-text-muted">
                          <img 
                            src="https://unpkg.com/pixelarticons@1.8.0/svg/download.svg" 
                            alt="Downloads" 
                            className="w-4 h-4"
                            style={{ filter: `brightness(0) saturate(100%) invert(60%)` }}
                          />
                          <span>{pluginStats[plugin.id].total_downloads || 0} downloads</span>
                        </div>
                      </div>
                    )}

                    {/* Flexible spacer */}
                    <div className="flex-1 min-h-0"></div>

                    {/* Footer Banners - Above buttons, fixed height */}
                    {bannersByPosition.footer.length > 0 && bannersByPosition.footer.filter(b => b.banner_type !== 'card').length > 0 && (
                      <div className="w-full mb-4 flex-shrink-0">
                        {bannersByPosition.footer.filter(b => b.banner_type !== 'card').map(banner => {
                          const { styles, className } = getBannerStyles(banner);
                          const alignmentClass = 
                            banner.alignment === 'center' ? 'justify-center' :
                            banner.alignment === 'right' ? 'justify-end' : 'justify-start';
                          
                          return (
                            <div key={banner.id} className={`w-full flex ${alignmentClass} mb-2`}>
                              <div 
                                className={`flex items-center gap-2 border-2 ${banner.full_width ? 'w-full' : ''} rounded-lg ${className}`}
                                style={styles}
                              >
                                {banner.icon_enabled && banner.icon_name && (
                                  <img 
                                    src={`https://unpkg.com/pixelarticons@1.8.0/svg/${banner.icon_name}.svg`}
                                    alt={banner.text}
                                    className="flex-shrink-0"
                                    style={{ 
                                      filter: banner.icon_filter,
                                      width: `${banner.icon_size}px`,
                                      height: `${banner.icon_size}px`
                                    }}
                                  />
                                )}
                                <span className="font-semibold">{banner.text}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Action buttons - Always at bottom, fixed height */}
                    <div className="flex gap-2 flex-shrink-0">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openModal(plugin);
                        }}
                        className="flex-1 h-12 bg-theme-primary text-white px-4 py-2 rounded-md hover:bg-opacity-80 transition-colors flex items-center justify-center font-medium"
                      >
                        View Details
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openModal(plugin);
                        }}
                        className="h-12 w-12 bg-theme-surface border border-theme-border text-theme-text rounded-md hover:bg-theme-surface-light transition-colors flex items-center justify-center flex-shrink-0"
                      >
                        <img 
                          src="https://unpkg.com/pixelarticons@1.8.0/svg/download.svg" 
                          alt="Download" 
                          className="w-4 h-4"
                          style={{ 
                            filter: 'brightness(0) saturate(100%) invert(40%)'
                          }}
                        />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Right Vertical Banners - Fixed positioning with padding */}
                {bannersByPosition['right-vertical'].length > 0 && bannersByPosition['right-vertical'][0].banner_type !== 'card' && (
                  <div className="flex flex-col justify-center py-4 pr-2 space-y-2 flex-shrink-0">
                    {bannersByPosition['right-vertical'].map(banner => {
                      const { styles, className } = getBannerStyles(banner);
                      return (
                        <div key={banner.id} className="transform rotate-90 origin-center">
                          <div 
                            className={`flex items-center gap-2 border-2 whitespace-nowrap ${className}`}
                            style={styles}
                          >
                            {banner.icon_enabled && banner.icon_name && (
                              <img 
                                src={`https://unpkg.com/pixelarticons@1.8.0/svg/${banner.icon_name}.svg`}
                                alt={banner.text}
                                className="flex-shrink-0"
                                style={{ 
                                  filter: banner.icon_filter,
                                  width: `${banner.icon_size}px`,
                                  height: `${banner.icon_size}px`
                                }}
                              />
                            )}
                            <span className="font-semibold text-xs">{banner.text}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {selectedPlugin && (
        <PluginDetailsModal
          plugin={selectedPlugin}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          hasDevAccess={hasDevAccess}
          onStatsUpdate={() => refreshPluginStats(selectedPlugin.id)}
        />
      )}
    </div>
  );
};

function App() {
  const { user, profile, loading: authLoading, signOut, isAdmin, hasDevAccess, profileLoading } = useAuth();
  const [currentPage, setCurrentPage] = useState<'home' | 'downloads' | 'profile' | 'admin' | 'auth-callback'>('home');
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState<'signin' | 'signup'>('signin');
  const [selectedPlugin, setSelectedPlugin] = useState<Plugin | null>(null);
  const [isPluginModalOpen, setIsPluginModalOpen] = useState(false);
  const [currentHomepage, setCurrentHomepage] = useState<'main' | 'about' | 'contact' | 'changelog' | 'features'>('main');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBuildType, setSelectedBuildType] = useState<string>('All');
  const [plugins, setPlugins] = useState<Plugin[]>([]);
  const [pluginsLoading, setPluginsLoading] = useState(true);
  const { getBannersForPlugin } = usePluginBanners();
  const { currentTheme } = useTheme();
  
  // Use the new plugin stats manager
  const { 
    pluginStats, 
    loadingStats, 
    loadAllPluginStats, 
    refreshPluginStats,
    refreshAllStats 
  } = usePluginStatsManager();

  // Load plugins from database
  useEffect(() => {
    loadPluginsFromDatabase();
  }, []);

  // Load stats when plugins are loaded
  useEffect(() => {
    if (plugins.length > 0) {
      loadAllPluginStats(plugins);
    }
  }, [plugins, loadAllPluginStats]);

  const loadPluginsFromDatabase = async () => {
    try {
      setPluginsLoading(true);
      
      // Load plugins from database
      const { data: dbPlugins, error: pluginsError } = await supabase
        .from('plugins')
        .select('*')
        .eq('enabled', true)
        .order('name');

      if (pluginsError) {
        console.error('Error loading plugins:', pluginsError);
        setPlugins([]); // Set empty array on error
        return;
      }

      // Load versions for each plugin to build the builds object
      const pluginsWithVersions = await Promise.all(
        (dbPlugins || []).map(async (plugin) => {
          const { data: versions, error: versionsError } = await supabase
            .from('plugin_versions')
            .select('version, build_type')
            .eq('plugin_id', plugin.id)
            .eq('enabled', true)
            .order('created_at', { ascending: false });

          if (versionsError) {
            console.error('Error loading versions for plugin:', plugin.id, versionsError);
          }

          // Group versions by build type
          const builds: { [key: string]: string[] } = {};
          (versions || []).forEach(version => {
            if (!builds[version.build_type]) {
              builds[version.build_type] = [];
            }
            builds[version.build_type].push(version.version);
          });

          // Convert database plugin to frontend Plugin interface
          return {
            id: plugin.id,
            name: plugin.name,
            description: plugin.description,
            lastUpdated: plugin.updated_at,
            color: getThemeColorForPlugin(plugin.id), // Use theme colors
            profile_picture_url: plugin.profile_picture_url,
            links: plugin.links || [], // Include the links array from database
            builds
          };
        })
      );

      setPlugins(pluginsWithVersions);
    } catch (error) {
      console.error('Error loading plugins from database:', error);
      setPlugins([]);
    } finally {
      setPluginsLoading(false);
    }
  };

  // Helper function to get theme-based colors for plugins
  const getThemeColorForPlugin = (pluginId: string) => {
    // Use theme secondary color for all plugins for consistency
    return currentTheme?.colors?.secondary || '#6366f1';
  };

  const themedPlugins = plugins.map((plugin: Plugin) => ({
    ...plugin,
    color: getThemeColorForPlugin(plugin.id)
  }));

  // Check if we're on the auth callback route
  const isAuthCallback = window.location.pathname === '/auth/callback';

  // If we're on the auth callback route, show the callback component
  if (isAuthCallback) {
    return <AuthCallback />;
  }

  const openAuthModal = (mode: 'signin' | 'signup') => {
    setAuthMode(mode);
    setIsAuthModalOpen(true);
  };

  const closeAuthModal = () => {
    setIsAuthModalOpen(false);
  };

  const getCurrentPageData = () => {
    switch (currentPage) {
      case 'home':
        return {
          title: 'Home',
          subtitle: 'Welcome to our homepage',
          icon: 'home',
          stats: 'Welcome visitor'
        };
      case 'downloads':
        return {
          title: 'Plugin Downloads',
          subtitle: 'Download the latest versions of your favorite plugins',
          icon: 'code',
          stats: `${plugins.length} plugins available`
        };
      case 'profile':
        return {
          title: 'Profile',
          subtitle: 'Manage your account settings',
          icon: 'user',
          stats: user ? `Welcome ${profile?.username || 'User'}` : 'Not logged in'
        };
      case 'admin':
        return {
          title: 'Admin Panel',
          subtitle: 'Manage users and dev build access',
          icon: 'gear',
          stats: `Admin Dashboard`
        };
      default:
        return {
          title: 'Home',
          subtitle: 'Welcome to our homepage',
          icon: 'home',
          stats: 'Welcome visitor'
        };
    }
  };

  const pageData = getCurrentPageData();

  return (
    <div className="min-h-screen bg-theme-background text-theme-text flex flex-col">
      {/* Header */}
      <header className="bg-theme-surface border-b-2 border-theme-border sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-theme-surface-light border-2 border-theme-border-light rounded-lg flex items-center justify-center">
                <img 
                  src={`https://unpkg.com/pixelarticons@1.8.0/svg/${pageData.icon}.svg`}
                  alt={pageData.icon} 
                  className="w-6 h-6"
                  style={{ filter: `brightness(0) saturate(100%) invert(1)` }}
                />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-theme-text">
                  {pageData.title}
                </h1>
                <p className="text-sm text-theme-text-secondary">{pageData.subtitle}</p>
              </div>
            </div>
            
            {/* Navigation & Auth */}
            <div className="flex items-center gap-4">
              <nav className="flex gap-2">
                <button
                  onClick={() => setCurrentPage('home')}
                  className={`px-4 py-2 rounded-lg transition-all duration-200 flex items-center gap-2 ${
                    currentPage === 'home'
                      ? 'bg-theme-primary text-white'
                      : 'bg-theme-surface-light text-theme-text-secondary hover:bg-theme-border-light'
                  }`}
                >
                  <img 
                    src="https://unpkg.com/pixelarticons@1.8.0/svg/home.svg" 
                    alt="Home" 
                    className="w-4 h-4"
                    style={{ filter: `brightness(0) saturate(100%) invert(1)` }}
                  />
                  Home
                </button>
                <button
                  onClick={() => setCurrentPage('downloads')}
                  className={`px-4 py-2 rounded-lg transition-all duration-200 flex items-center gap-2 ${
                    currentPage === 'downloads'
                      ? 'bg-theme-secondary text-white'
                      : 'bg-theme-surface-light text-theme-text-secondary hover:bg-theme-border-light'
                  }`}
                >
                  <img 
                    src="https://unpkg.com/pixelarticons@1.8.0/svg/download.svg" 
                    alt="Downloads" 
                    className="w-4 h-4"
                    style={{ filter: `brightness(0) saturate(100%) invert(1)` }}
                  />
                  Downloads
                </button>
                {isAdmin && (
                  <button
                    onClick={() => setCurrentPage('admin')}
                    className={`px-4 py-2 rounded-lg transition-all duration-200 flex items-center gap-2 ${
                      currentPage === 'admin'
                        ? 'bg-red-600 text-white'
                        : 'bg-theme-surface-light text-theme-text-secondary hover:bg-theme-border-light'
                    }`}
                  >
                    <img 
                      src="https://unpkg.com/pixelarticons@1.8.0/svg/gear.svg" 
                      alt="Admin" 
                      className="w-4 h-4"
                      style={{ filter: `brightness(0) saturate(100%) invert(1)` }}
                    />
                    Admin
                  </button>
                )}
              </nav>
              
              {/* Auth Section */}
              {authLoading || (user && profileLoading && !profile) ? (
                <div className="flex items-center gap-2 text-theme-text-secondary">
                  <div className="w-4 h-4 border-2 border-theme-text-secondary border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-sm">Loading...</span>
                </div>
              ) : user ? (
                <ProfileDropdown onProfileClick={() => setCurrentPage('profile')} />
              ) : (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openAuthModal('signin')}
                    className="px-4 py-2 bg-theme-surface-light text-theme-text-secondary hover:bg-theme-primary hover:text-white transition-all duration-200 rounded-lg flex items-center gap-2"
                  >
                    <img 
                      src="https://unpkg.com/pixelarticons@1.8.0/svg/login.svg" 
                      alt="Sign In" 
                      className="w-4 h-4"
                      style={{ filter: `brightness(0) saturate(100%) invert(1)` }}
                    />
                    Sign In
                  </button>
                  <button
                    onClick={() => openAuthModal('signup')}
                    className="px-4 py-2 bg-theme-primary text-white hover:bg-theme-secondary transition-all duration-200 rounded-lg flex items-center gap-2"
                  >
                    <img 
                      src="https://unpkg.com/pixelarticons@1.8.0/svg/user-plus.svg" 
                      alt="Sign Up" 
                      className="w-4 h-4"
                      style={{ filter: `brightness(0) saturate(100%) invert(1)` }}
                    />
                    Sign Up
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Page Content */}
      <div className="flex-1">
        {currentPage === 'home' && <HomePage />}
        {currentPage === 'downloads' && <DownloadsPage hasDevAccess={hasDevAccess} plugins={plugins} pluginsLoading={pluginsLoading} pluginStats={pluginStats} loadingStats={loadingStats} refreshPluginStats={refreshPluginStats} />}
        {currentPage === 'profile' && <ProfilePage onNavigateHome={() => setCurrentPage('home')} />}
        {currentPage === 'admin' && <AdminPage />}
      </div>

      {/* Footer */}
      <footer className="bg-theme-surface border-t-2 border-theme-border mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center text-theme-text-secondary">
            <p>&copy; 2025 JunkStudios. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Auth Modal */}
              <AuthModal
          isOpen={isAuthModalOpen}
          onClose={closeAuthModal}
          initialMode={authMode}
        />

      {/* Auth Error Handler */}
      <AuthErrorHandler />

      {/* Auth Debug Info (Development only) */}
      <AuthDebugInfo />
    </div>
  );
}

export default App;